import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export default function InvoiceView() {
  const { invId } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);

  useEffect(() => {
    api.get(`/invoices/${invId}`).then((r) => setInv(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [invId]);

  if (!inv) return <div className="py-20 text-center text-sm text-muted-foreground">Loading invoice…</div>;

  return (
    <div data-testid="invoice-page" className="mx-auto max-w-2xl">
      <div className="mb-4 flex items-center justify-between print:hidden">
        <button data-testid="invoice-back-button" onClick={() => navigate(`/bookings/${inv.booking_id}`)} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-ink">
          <ArrowLeft className="h-4 w-4" /> Back to booking
        </button>
        <Button data-testid="invoice-print-button" onClick={() => window.print()} className="rounded-md bg-ink hover:bg-ink-light">
          <Printer className="mr-2 h-4 w-4" /> Print Invoice
        </Button>
      </div>

      <div className="border border-border bg-white p-8">
        <div className="flex items-start justify-between border-b-2 border-ink pb-4">
          <div>
            <h1 className="font-heading text-2xl font-black tracking-tight">INVOICE</h1>
            <p className="font-mono text-sm font-bold" data-testid="invoice-number">{inv.invoice_number}</p>
          </div>
          <div className="text-right text-sm">
            <p className="font-heading font-bold">Fleet &amp; Feast Catering Ltd</p>
            <p className="text-muted-foreground">1 Depot Lane, London E1 6AN</p>
            <p className="text-muted-foreground">accounts@fleetfeast.example</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Bill to</p>
            <p className="mt-1 font-medium" data-testid="invoice-customer">{inv.customer_name}</p>
            <p className="text-muted-foreground">Booking {inv.booking_ref}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Details</p>
            <p className="mt-1">Issued: {format(new Date(inv.created_at), "dd MMM yyyy")}</p>
            <p>Event date: <span className="tabular-nums">{inv.event_date}</span></p>
            <StatusBadge status={inv.status} />
          </div>
        </div>

        <div className="overflow-x-auto">
        <table className="mt-6 w-full text-sm">
          <thead>
            <tr className="border-b-2 border-ink text-left">
              <th className="py-2 text-xs font-semibold uppercase tracking-[0.15em]">Description</th>
              <th className="py-2 text-right text-xs font-semibold uppercase tracking-[0.15em]">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.line_items.map((li, i) => (
              <tr key={i} className="border-b border-border" data-testid={`invoice-line-${i}`}>
                <td className="py-2.5">{li.label}</td>
                <td className="py-2.5 text-right tabular-nums">{fmt(li.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        <dl className="mt-4 ml-auto w-64 space-y-1.5 text-sm">
          <div className="flex justify-between"><dt className="text-muted-foreground">Total</dt><dd className="font-bold tabular-nums" data-testid="invoice-total">{fmt(inv.total)}</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Paid to date</dt><dd className="tabular-nums">{fmt(inv.amount_paid)}</dd></div>
          <div className="flex justify-between border-t-2 border-ink pt-1.5"><dt className="font-heading font-bold">Balance due</dt><dd className="font-heading font-bold text-brand tabular-nums" data-testid="invoice-balance">{fmt(inv.balance)}</dd></div>
        </dl>

        <p className="mt-8 border-t border-border pt-4 text-xs text-muted-foreground">
          Payment terms: balance due 7 days before the event date. Bank transfer to Fleet &amp; Feast Catering Ltd, or pay online via your client portal link.
        </p>
      </div>
    </div>
  );
}
